import React, { useRef, useEffect, useCallback } from 'react';
import T from 'prop-types';
import classnames from 'classnames';
import Spinner from '../Spinner';
import Suggestions from './suggestions';
import useOpenClose from './useOpenClose';
import Button from './button';
import ErrorMessage from './error';

function Field(props) {
  const {
    placeholder,
    label,
    labelClassName,
    id,
    value,
    name,
    isLoading,
    highlightSelection,
    inputClassName,
    innerClassName,
    hightlightTitle,
    className,
  } = props;

  // Create DOM refs to elements
  const containerElement = useRef(null);
  const inputElement = useRef(null);
  const suggestionsElement = useRef(null);

  const [
    suggestionIsOpen,
    openSuggestion,
    closeSuggestion,
    setSuggestionIsOpen,
  ] = useOpenClose(false);

  useEffect(() => {
    props.setStatus({ isLoading: isLoading });
  }, [isLoading]);

  // About why useCallback here
  // https://reactjs.org/docs/hooks-faq.html#is-it-safe-to-omit-functions-from-the-list-of-dependencies
  const delayedClose = useCallback(
    () => setTimeout(() => closeSuggestion(), 0),
    [closeSuggestion]
  );

  const handleBlur = (e) => {
    props.onBlur(e);
  };

  const handleChange = (e) => {
    // from find-care-form
    props.onValueChange(e);
    // Autocomplete
    props.handleChange(e);
    // Formik
    props.onChange(e);
    openSuggestion();
  };
  /* callback when clear field is clicked */
  const clearField = () => {
    if (props.clearField) {
      props.clearField();
    }
    props.setValue(name, '');
  };

  const handleClickOutside = (el, fn) => (e) => {
    // Do nothing when we click inside the element
    if (el && el.current && el.current.contains(e.target)) {
      return;
    }
    fn();
  };

  const getItems = (props) => {
    if (typeof props.getItems === 'function') {
      return props.getItems(props);
    }
    return props.items;
  };

  const b = Boolean(value.length > 0);

  const displayZeroHitsMessage = Boolean(
    props.zeroHits && props.zeroHitsMessage
  );

  const suggestionsProps = {
    highlightSelection: highlightSelection && props.isSelected,
    hightlightTitle,
    id: `suggestions_${name}`,
    ref: suggestionsElement,
    className: `${className}__autocomplete`,
    selected: value,
    isDefault: !b,
    useCloseButton: props.suggestCloseButton,
    isOpen: suggestionIsOpen && !displayZeroHitsMessage && !props.error,
    footer: props.suggestionsFooter,
    items: b ? getItems(props) : props.defaultSuggestions,
    defaultItems: props.defaultSuggestions,
    defaultTitle: props.defaultSuggestionsTitle,
    title: b ? props.suggestionsTitle : props.defaultSuggestionsTitle,
  };

  useEffect(() => {
    document.addEventListener(
      'mousedown',
      handleClickOutside(containerElement, delayedClose)
    );
    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside(containerElement, delayedClose)
      );
    };
  }, [containerElement, delayedClose]);

  const hasDefaults = props.defaultSuggestions.length > 0;

  const outerClassName = classnames(className, {
    [`${className}--is-loading`]: value.length > 0 && props.isLoading,
    [`${className}--has-defaults`]: hasDefaults,
    [`${className}--has-error`]: props.error,
    [`${className}--has-value`]: value.length > 0,
    [`${className}--suggestions-is-open`]: suggestionIsOpen && value.length > 2,
  });

  useEffect(() => {
    if (
      !props.doNotAutoselectFirstItemOnClose &&
      !suggestionIsOpen &&
      !props.isSelected &&
      props.value.length >= props.minStringLength
    ) {
      const items = getItems(props).filter((item) => !item.excludeAutoselect);
      if (items.length > 0) {
        const item = items[0];
        props.setValue(name, item.text || item.value);
        if (typeof props.onSuggestionSelect === 'function') {
          props.onSuggestionSelect(item);
        }
      }
    }
  }, [suggestionIsOpen]);

  useEffect(() => {
    if (
      containerElement &&
      containerElement.current &&
      !containerElement.current.contains(props.activeElement)
    ) {
      if (suggestionIsOpen) {
        delayedClose();
      }
    }
  }, [props.activeElement]);

  return (
    <div className={outerClassName} ref={containerElement} id={`field_${name}`}>
      <label className={labelClassName} htmlFor={id || name}>
        {label}
      </label>
      <div className={innerClassName}>
        <input
          aria-controls={suggestionsProps.id}
          className={inputClassName}
          ref={inputElement}
          id={id || name}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          type="text"
          autoComplete="off"
        />

        {props.error && (
          <ErrorMessage
            className={props.errorClassName || `${className}__error`}
            text={props.error}
          />
        )}
        {displayZeroHitsMessage && (
          <ErrorMessage
            className={props.errorClassName || `${className}__error`}
            text={props.zeroHitsMessage}
          />
        )}

        <Suggestions
          {...suggestionsProps}
          close={(event) => {
            delayedClose();
            event.target.blur();
          }}
          handleClick={(item) => (event) => {
            props.setValue(name, item.value);
            delayedClose();
            event.target.blur();
            if (typeof props.onSuggestionSelect === 'function') {
              props.onSuggestionSelect(item);
            }
          }}
        />

        <Spinner />

        {value.length > 0 && (
          <Button
            textOnlySr={true}
            className={`${className}__clear-field ${
              props.defaultSuggestions.length > 0
                ? `${className}__clear-field--toggle-sibling`
                : ''
            }`}
            text="Rensa fält"
            onClick={clearField}
          />
        )}
        {props.defaultSuggestions.length > 0 && (
          <Button
            textOnlySr={true}
            aria-controls={`container_${suggestionsProps.id}`}
            aria-haspopup="true"
            aria-expanded={suggestionIsOpen}
            className={classnames({
              [`${className}__toggle-suggestion`]: true,
              [`${className}__toggle-suggestion--is-open`]: suggestionIsOpen,
            })}
            text={suggestionIsOpen ? 'Dölj förslag' : 'Visa förslag'}
            onClick={() => {
              if (!suggestionIsOpen) {
                // Set focus to the first button
                // in the suggestions panel
                const firstButton =
                  suggestionsElement.current.querySelector('[data-element]');

                if (firstButton) {
                  firstButton.focus();
                } else {
                  inputElement.current.focus();
                }
              }
              // Toggle suggestion panel
              setSuggestionIsOpen(!suggestionIsOpen);
            }}
          />
        )}
      </div>
    </div>
  );
}

Field.propTypes = {
  id: T.string,
  onBlur: T.func,
  onChange: T.func,
  /** Function is run when an item is clicked in suggestions. */
  onSuggestionSelect: T.func,
  value: T.string,
  suggestionsFooter: T.func,
  suggestions: T.array,
  defaultSuggestionsTitle: T.string,

  suggestionsTitle: T.string,
  defaultSuggestions: T.array,
  suggestCloseButton: T.bool,
  setValue: T.func,
  /* input element placeholder attribute */
  placeholder: T.string,
  /* div with buttons and field */
  innerClassName: T.string,
  /* input element */
  inputClassname: T.string,
  /* html label element */
  labelClassName: T.string,
  /* input element name attribute */
  name: T.string.isRequired,
  /* callback when clear field is clicked */
  clearField: T.func,
  /* should selected item be highlighed before other items */
  highlightSelection: T.bool,
};

Field.defaultProps = {
  className: 'field',
  labelClassName: 'field__label',
  suggestionsFooter: null,
  placeholder: '',
  suggestions: [],
  defaultSuggestions: [],
  onValueChange: () => {},
  suggestCloseButton: false,
};

export default Field;
